import { Alert, Button, GenericFullscreenDialog } from '@/common/components'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useUpdatePartnershipOperatorDetails, Partnership } from '@/features/suppliers/hooks/partnerships'
import toast from 'react-hot-toast'
import SupplierForm from '../../../components/SupplierForm'
import { validateSupplierForm, SupplierFormData } from '../../../components/SupplierForm.schema'

export default function SupplierEditDialog({
  isOpen,
  close,
  partnership,
  organizationId
}: {
  isOpen: boolean
  close: () => void
  partnership: Partnership | null
  organizationId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: updatePartnership, isPending: isSaving } =
    useUpdatePartnershipOperatorDetails(organizationId, partnership?.id || '')

  const [formData, setFormData] = useState<SupplierFormData>({
    isBusinessRegistrationNumberNotKnown: false,
    supplierName: '',
    businessRegistrationNumber: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    subDivision: '',
    country: '',
    contactName: '',
    contactPhoneNumber: '',
    contactEmail: ''
  })

  const resetFormData = useCallback(() => {
    if (partnership) {
      setFormData({
        isBusinessRegistrationNumberNotKnown: !partnership.operatorBusinessRegistrationNumber,
        supplierName: partnership.name,
        businessRegistrationNumber: partnership.operatorBusinessRegistrationNumber || '',
        street: partnership.operatorStreet || '',
        streetNumber: partnership.operatorStreetNumber || '',
        street2: partnership.operatorStreet2 || '',
        postCode: partnership.operatorPostCode || '',
        city: partnership.operatorCity || '',
        poBox: partnership.operatorPoBox || '',
        subDivision: partnership.operatorSubDivision || '',
        country: partnership.country,
        contactName: partnership.operatorContactName || '',
        contactPhoneNumber: partnership.operatorContactPhoneNumber || '',
        contactEmail: partnership.operatorContactEmail || ''
      })
    }
  }, [partnership])

  useEffect(() => {
    resetFormData()
  }, [resetFormData])

  const onClose = useCallback(() => {
    resetFormData()
    close()
  }, [close, resetFormData])

  const isSaveDisabled = useMemo(() => {
    const validation = validateSupplierForm(formData)
    return isSaving || !validation.success
  }, [isSaving, formData])

  const onSave = useCallback(async () => {
    if (!partnership) {return}

    try {
      await updatePartnership({
        name: formData.supplierName,
        country: formData.country,
        operatorStreet: formData.street,
        operatorStreet2: formData.street2,
        operatorStreetNumber: formData.streetNumber,
        operatorSubDivision: formData.subDivision,
        operatorPostCode: formData.postCode,
        operatorPoBox: formData.poBox,
        operatorCountry: formData.country,
        operatorCity: formData.city,
        operatorBusinessRegistrationNumber: formData.businessRegistrationNumber,
        operatorContactName: formData.contactName,
        operatorContactPhoneNumber: formData.contactPhoneNumber,
        operatorContactEmail: formData.contactEmail
      })
      toast.success(t('suppliers:editSupplierDialog.notifications.supplierUpdatedSuccessfully'), {
        position: 'top-right'
      })
    } catch (error) {
      toast.error(t('suppliers:editSupplierDialog.notifications.failedToUpdateSupplier'), {
        position: 'top-right'
      })
      console.error(error)
    }
    onClose()
  }, [t, updatePartnership, partnership, formData, onClose])

  if (!partnership) {
    return null
  }

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={onClose}
      title={t('suppliers:editSupplierDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('suppliers:editSupplierDialog.actions.save')}
          </Button>
        </div>
      }
    >
      <Alert type="info" className="mb-4">
        {t('suppliers:editSupplierDialog.info')}
        This section contains organisation-level information for the supplier. Details of the
        specific installation will be captured at the next step.
      </Alert>
      <SupplierForm data={formData} onChange={setFormData} />
    </GenericFullscreenDialog>
  )
}
